import { logEvent } from '$lib/analyticsPosthog';
import { deduplicateObjects } from '$lib/common';
import { Attending, ContactPreference } from '$lib/eventTypes';
import { _deleteGuestsRelations, _deleteItems } from '$lib/server/event';
import {
	deleteGuests,
	deleteItems,
	getEventBySecret,
	isUserOwnerOfEvent,
	updateBroadcast,
	upsertGuests,
	upsertItems,
	upsertUsers,
	upsertVisitor,
	type UiEvent,
	type Guest,
} from '$lib/server/supabase';
import { getEventById } from '$lib/server/supabase';
import { getContext } from 'telefunc';
import { denialWords, sanitizeNameAndCompanions } from './eventCommon';
import { routes, withHost } from '$lib/routes';
import { sendEmailsInBatch } from '$lib/server/email';
import { sendSms } from '$lib/server/sms';

//
// IMPORTANT!
// When calling these functions ALWAYS use object literals as parameters, to surface type errors. Otherwise the generated shild will fail requests only in production
// see: https://github.com/brillout/telefunc/issues/81

export async function removeBringItem(eventId: string, itemId: string) {
	const { userId } = getContext();
	logEvent('bring_item_removed', { eventId, userId });
	return _deleteItems(eventId, userId, [itemId]);
}

export async function addBringItem(eventId: string, name: string) {
	const { userId } = getContext();
	logEvent('bring_item_added', { eventId, userId });
	return upsertItems(eventId, [{ userId, name }]);
}

export async function removeMe(eventId: string) {
	const { userId } = getContext();
	logEvent('guest_removed', { eventId, userId });
	await _deleteGuestsRelations(eventId, userId);
	await deleteGuests(eventId, [userId]);
	await handleWaitlistMoveUp(eventId);
}

export async function addMeAsVisitor(eventId: string) {
	const { userId } = getContext();
	logEvent('visitor_added', { eventId, userId });
	return upsertVisitor(eventId, userId);
}

export async function setMeAsAttending(
	eventId: string,
	guest: GuestToWrite
) {
	const { userId } = getContext();
	logEvent('guest_attends', { eventId: eventId, userId: userId });
	return _upsertGuest(eventId, Attending.ATTENDING, { ...guest, userId });
}

export async function setMeAsMaybeAttending(
	eventId: string,
	guest: Omit<GuestToWrite, "payedAllegedly">
) {
	const { userId } = getContext();
	logEvent('guest_attends_maybe', { eventId: eventId, userId: userId });
	return _upsertGuest(eventId, Attending.MAYBE, { ...guest, userId, payedAllegedly: undefined });
}


export async function setMeToWaitlist(
	eventId: string,
	guest: Omit<GuestToWrite, "payedAllegedly">
) {
	const { userId } = getContext();
	logEvent('guest_added_on_waitlist', { eventId: eventId, userId: userId });
	return _upsertGuest(eventId, Attending.WAITLIST, { ...guest, userId, payedAllegedly: null });
}

export async function setMeAsNotAttending(eventId: string, guest: { name: string; comment: string; }) {
	const { userId } = getContext();
	const { name } = sanitizeNameAndCompanions(guest.name, []);

	logEvent('guest_attends_not', { eventId: eventId, userId: userId });
	await _deleteGuestsRelations(eventId, userId);
	await upsertGuests(eventId, [
		{
			name,
			userId,
			attending: Attending.NOT_ATTENDING,
			comment: guest.comment,
		},
	])
	await handleWaitlistMoveUp(eventId);
}

export async function removeGuest(secret: string | undefined, userId: string) {
	const event = await getEventBySecret(secret);
	if (event.ownerSecret !== secret) throw 'Invalid secret';

	await _deleteGuestsRelations(event.id, userId);
	await deleteGuests(event.id, [userId]);
	await handleWaitlistMoveUp(event.id);
}

export async function addGuest(secret: string | undefined, guest: { name: string; attending: Attending }) {
	const event = await getEventBySecret(secret);
	if (event.ownerSecret !== secret) throw 'Invalid secret';

	//TODO remove?
	const createdUser = (await upsertUsers([{ name: guest.name }]))?.[0];

	return upsertGuests(event.id, [
		{
			userId: createdUser.id,
			attending: guest.attending,
			name: guest.name
		},
	]);
}


export async function moveUpWaitlistEntry(secret: string, guestId: string) {
	const event = await getEventBySecret(secret);
	if (event.ownerSecret !== secret) throw 'Invalid secret';

	await upsertGuests(event.id, [{ userId: guestId, attending: Attending.ATTENDING, }]);
	await notifyGuestsOfMovingUp(event, event.guests.filter((x) => x.userId === guestId))
}

export async function updateGuest(secret: string, guest: { userId: string; name: string; attending: Attending }) {
	const event = await getEventBySecret(secret);
	if (!event) throw 'Invalid secret';

	if (guest.attending === Attending.NOT_ATTENDING) {
		await _deleteGuestsRelations(event.id, guest.userId);
	}

	await upsertGuests(event.id, [guest]);
	await handleWaitlistMoveUp(event.id);
}

export async function unpinBroadcast(eventId: string, broadcastId: number) {
	const { userId } = getContext();
	if (!await isUserOwnerOfEvent(eventId, userId)) throw 'You are not the owner of this event';

	return updateBroadcast({ id: broadcastId, pinned: false });
}




export type GuestToWrite = {
	name: string;
	comment: string;
	bringItems: { name: string; id?: string }[];
	companions: string[];
	payedAllegedly: number | null | undefined;
	contactPreference: ContactPreference;
	email?: string;
	phone?: string;
	answers: { questionId: number; answer?: string }[];
}

////////////////////////////////////
///////////// helpers //////////////
////////////////////////////////////

async function _upsertGuest(
	eventId: string,
	attending: Attending,
	guest: GuestToWrite & { userId: string }
) {
	const event = await getEventById(eventId);
	const eventFull = event.maxGuests && event.guests.length >= (event.maxGuests ?? -1);
	if (eventFull && (attending === Attending.ATTENDING || attending === Attending.MAYBE)) {
		attending = Attending.WAITLIST;
	}
	const { name, companions } = sanitizeNameAndCompanions(guest.name, guest.companions);

	const answers = guest.answers.filter(x => x.answer?.trim()).reduce((acc, x) => {
		acc[x.questionId] = x.answer!!;
		return acc;
	}, {} as Record<number, string>);

	const userToWrite = {
		attending,
		comment: guest.comment,
		userId: guest.userId, // TODO WHAT?
		companions,
		name,
		paidAllegedly: guest.payedAllegedly,
		contactPreference: guest.contactPreference,
		email: guest.email,
		phone: guest.phone,
		answers,
	}
	if (guest.payedAllegedly === undefined) {
		delete userToWrite.paidAllegedly;
	}

	await Promise.all([
		_setBringItems(eventId, guest.userId, guest.bringItems),
		upsertGuests(eventId, [userToWrite])
	])

	return { name, attending };
}

async function handleWaitlistMoveUp(eventId: string) {
	const event = await getEventById(eventId);
	const freeSpots = (event.maxGuests ?? 0) - event.guests.filter((x) => x.attending === Attending.ATTENDING).length;
	if (freeSpots <= 0) return;

	const waitlist = event.guests.filter((x) => x.attending === Attending.WAITLIST).sort((a, b) => new Date(a.createdAt!).getTime() - new Date(b.createdAt!).getTime());
	const guestsToMove = waitlist.slice(0, freeSpots);
	await upsertGuests(eventId, guestsToMove.map(x => {
		delete x.user
		return { ...x, attending: Attending.ATTENDING }
	}))
	await notifyGuestsOfMovingUp(event, guestsToMove)
}

async function notifyGuestsOfMovingUp(event: UiEvent, guests: Guest[]) {
	const emailGuests = guests.filter((x) => x.contactPreference === ContactPreference.EMAIL && x.email);
	const phoneGuests = guests.filter((x) => x.contactPreference === ContactPreference.PHONE && x.phone);
	const emailSubject = `${event.name} hat dich auf die Gästeliste gesetzt`
	const email = `Herzlichen Glückwunsch! Der Event <a href="${withHost(routes.event(event.slug))}">${event.name}</a> hat dich auf die Gästeliste gesetzt. Du kannst jetzt teilnehmen.
<br>‼️ Falls du nicht mehr teilnehmen möchtest, melde dich bitte auf der Website ab.
<br><br>
Zum Event: <a href="${withHost(routes.event(event.slug))}">${withHost(routes.event(event.slug))}</a>
<br><br>`;

	const sms = `Der Event ${event.name} hat dich auf die Gästeliste gesetzt. Du kannst jetzt teilnehmen.
Herzlichen Glückwunsch!
‼️ Falls du nicht mehr teilnehmen möchtest, melde dich bitte auf der Website ab.

Zum Event: ${withHost(routes.event(event.slug))}`

	await Promise.all([
		sendEmailsInBatch(emailGuests.map((x) => x.email!), emailSubject, email),
		sendSms(phoneGuests.map((x) => x.phone!), sms),
	]);
}

async function _setBringItems(eventId: string, userId: string, items: { name: string; id?: string }[]) {
	logEvent('bring_item_added', { eventId: eventId, userId: userId });
	items = items.filter((x) => x?.name.trim());
	items = deduplicateObjects(items, 'name'); // deduplicating cuz some users go: lets see how many things i can add
	items = filterOutDenialWords(items, 'name'); // remove denial words

	const event = await getEventById(eventId);
	const newItems = items.filter((x) => !x.id).map((x) => ({ ...x, userId: userId }));
	const changedItems = items
		.filter((x) => x.id && event.items.some((y) => y.id === x.id && y.name !== x.name && y.userId === userId))
		.map((x) => ({ ...x, userId: userId }));
	const removedItems = event.items
		.filter((x) => x.id && x.userId === userId && !items.some((y) => y.id === x.id))
		.map((x) => x.id);

	await Promise.all([
		upsertItems(eventId, newItems),
		upsertItems(eventId, changedItems), // upsert does not handle insert and update in the same array yet: https://github.com/supabase/postgrest-js/issues/173
		deleteItems(removedItems),
	]);
}


function filterOutDenialWords<T extends Record<string, unknown>>(array: Array<T>, property: keyof T) {
	return array.filter((x) => {
		const value = (x[property] as string).toLowerCase().replace(/[^a-zA-Z0-9äöüÄÖÜß]/g, '');
		return !denialWords.includes(value);
	});
}
